import React, { useCallback, useMemo, useState, useEffect } from 'react';
import capitalize from 'lodash/capitalize';
import { Text, Stack } from '@atlaskit/primitives';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { DynamicResultTable } from '@css-xp/base';
import { CloudProductDetailView, EntitlementStatus, getBilledForColumnData } from '@css-xp/base/entitlement-utils';
import { getFormattedDate } from '@css-xp/entitlements-v2';
import {
  CloudSiteLink,
  emptyStateProp,
  errorStateProp,
  NoData,
  OrgNameLink,
  TableHeading,
  genericEmptyStateProp,
  NextBillDate,
  Usage,
} from '@css-xp/ppc-common';
import { routePaths } from '@css-xp/ppc-constants';
import {
  getRouteUrl,
  isFutureDatedInactiveSubscription,
  isOptedQuantitySourceInherited,
  isOrgProduct,
} from '@css-xp/ppc-utils';
import {
  FetchLinkedEntitlementsPpcQuery,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  BillingStatus,
} from '@css-xp/graphtypes/src/generated-types-new';
import { AppsCountCell as AppsCount, IncludedProductsCell } from '@css-xp/ppc-subscription-table';

import { useFetchLinkedEntitlementsPPCQuery } from '../../../hooks/use-fetch-linked-entitlements-ppc-query';
import {
  EntitlementPageTabsColumnConfigurationType,
  EntitlementPageTabsColumnKeys,
} from '../entitlement-page-tabs/table-column-configuration';

interface LinkedEntitlementsTabV2Props {
  entitlementId: string | undefined;
  containerId?: string | undefined;
  currentEntitlementType: LinkedEntitlementTypes | undefined;
  destinationEntitlementType: Array<LinkedEntitlementTypes>;
  productType: LinkedProductTypes;
  findProducts?: boolean;
  findApps?: boolean;
  skipOption?: boolean;
  transactionAccountId: string;
  isDesignIteration1Enabled?: boolean;
  tableHeading?: string;
  tableColumns: EntitlementPageTabsColumnConfigurationType[];
  isAppificationEnabled?: boolean;
}

const getDataForCurrentPage = (
  currentPage: number,
  pageSize: number,
  data: FetchLinkedEntitlementsPpcQuery['entitlementDetails']['linkedEntitlements']
) => {
  if (!data || data.length === 0) return [];
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data?.slice(start, end);
};

const getShouldTruncate = (shouldTruncate?: boolean) => {
  if (shouldTruncate) {
    return { shouldTruncate: shouldTruncate };
  }
  return {};
};

export const LinkedEntitlementsTabV2 = ({
  entitlementId,
  containerId,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findProducts,
  findApps,
  skipOption,
  transactionAccountId,
  isDesignIteration1Enabled,
  tableHeading,
  tableColumns,
  isAppificationEnabled,
}: LinkedEntitlementsTabV2Props) => {
  const [tableViewData, setTableViewData] = useState<{
    data: FetchLinkedEntitlementsPpcQuery['entitlementDetails']['linkedEntitlements'];
    currentPage: number;
    rowsPerPage: number;
  }>({
    data: [],
    currentPage: 1,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  });

  const { loading, data, error } = useFetchLinkedEntitlementsPPCQuery({
    entitlementId: entitlementId || '',
    containerId: containerId,
    currentEntitlementType: currentEntitlementType,
    destinationEntitlementType: destinationEntitlementType,
    productType: productType,
    findProducts: findProducts,
    findApps: findApps,
    skipOption: skipOption,
    transactionAccountId: transactionAccountId,
  });

  const linkedEntitlements: FetchLinkedEntitlementsPpcQuery['entitlementDetails']['linkedEntitlements'] = useMemo(
    () => data?.entitlementDetails?.linkedEntitlements || [],
    [data?.entitlementDetails?.linkedEntitlements]
  );
  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });

  useEffect(
    () =>
      setTableViewData({
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        currentPage: 1,
        data: getDataForCurrentPage(1, DEFAULT_ROWS_PER_PAGE, linkedEntitlements),
      }),
    [linkedEntitlements]
  );

  const handleRowSizeChange = useCallback(
    (rowSize: number) => {
      setTableViewData({
        currentPage: 1,
        rowsPerPage: rowSize,
        data: getDataForCurrentPage(1, rowSize, linkedEntitlements),
      });
    },
    [linkedEntitlements]
  );

  const handlePageChange = useCallback(
    (pageNo: number) => {
      setTableViewData({
        ...tableViewData,
        currentPage: pageNo,
        data: getDataForCurrentPage(pageNo, tableViewData.rowsPerPage, linkedEntitlements),
      });
    },
    [tableViewData, linkedEntitlements]
  );

  const renderEntitlementDetails = useCallback(
    (linkedEntitlement: any) => (
      <CloudProductDetailView
        productId={linkedEntitlement?.productId as string}
        entitlementNumber={linkedEntitlement?.entitlementNumber || ''}
        edition={linkedEntitlement?.edition}
        provisioningStatus={linkedEntitlement?.provisioningStatus}
        productName={linkedEntitlement?.productName}
        cloudSiteId={linkedEntitlement?.cloudSiteId}
        trial={linkedEntitlement?.trial ?? false}
        offeringPricingType={linkedEntitlement?.offering?.pricingType as string}
        isApp={linkedEntitlement?.isApp ?? false}
        isSandbox={linkedEntitlement?.isSandbox ?? false}
        productLink={linkedEntitlement?.entitlementNumber ?? ''}
        version={isDesignIteration1Enabled ? 2 : undefined}
        isAddOn={!!linkedEntitlement?.isAddOn}
        shouldHideEntitlementDetails={isDesignIteration1Enabled}
        shouldHideExtraInfo
        logoId={linkedEntitlement?.logoId}
      />
    ),
    [isDesignIteration1Enabled]
  );

  const renderEntitlementStatus = useCallback((linkedEntitlement: any) => {
    const isFutureDatedInActiveEntitlement = isFutureDatedInactiveSubscription(
      !!linkedEntitlement?.isFutureDatedSubscription,
      linkedEntitlement?.billingStatus as BillingStatus
    );

    return (
      <EntitlementStatus
        billingStatus={linkedEntitlement?.billingStatus}
        provisioningStatus={linkedEntitlement?.provisioningStatus}
        scheduledChanges={linkedEntitlement?.scheduledChanges}
        isFutureDatedInactiveEntitlement={isFutureDatedInActiveEntitlement}
        hideInactiveStatus={isFutureDatedInActiveEntitlement}
      />
    );
  }, []);

  const renderCloudSiteLink = useCallback(
    (linkedEntitlement: any) => (
      <CloudSiteLink
        siteName={linkedEntitlement?.cloudSiteHostname}
        txaId={transactionAccountId}
        cloudSiteId={linkedEntitlement?.cloudSiteId}
        isOrgProductEntitlement={
          isDesignIteration1Enabled &&
          isOrgProduct({
            productIdOrKey: linkedEntitlement?.productId,
            offeringName: linkedEntitlement?.edition,
            relatesFromEntitlements: linkedEntitlement?.relatesFromEntitlements,
            offering: linkedEntitlement?.offering,
          })
        }
      />
    ),
    [transactionAccountId, isDesignIteration1Enabled]
  );

  const getHead = useCallback(() => {
    return {
      cells: tableColumns.map((column) => {
        switch (column.columnKey) {
          case EntitlementPageTabsColumnKeys.SUBSCRIPTION:
            return {
              content: column.columnLabel || 'Subscription',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.STATUS:
            return {
              content: column.columnLabel || 'Status',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.APPS:
            return {
              content: column.columnLabel || 'Apps',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.ORG_NAME:
            return {
              content: column.columnLabel || 'Org name',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.SITE:
            return {
              content: column.columnLabel || 'Site',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.USAGE:
            return {
              content: column.columnLabel || 'Usage',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.BILLING_CYCLE:
            return {
              content: column.columnLabel || 'Billing cycle',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.CREATED:
            return {
              content: column.columnLabel || 'Created',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.ENTITLEMENT_NUMBER:
            return {
              content: column.columnLabel || 'Entitlement number',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.PLAN:
            return {
              content: column.columnLabel || 'Plan',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.QUANTITY:
            return {
              content: column.columnLabel || 'Quantity',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.RENEW_DATE:
            return {
              content: column.columnLabel || 'Renew date',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
          case EntitlementPageTabsColumnKeys.LINKED_SUBSCRIPTIONS:
            return {
              content: column.columnLabel || 'Linked subscriptions',
              width: column.width,
              ...getShouldTruncate(column.shouldTruncate),
            };
        }
      }),
    };
  }, [tableColumns]);

  const getRows = useCallback(() => {
    return tableViewData.data?.map((linkedEntitlement) => {
      return {
        cells: tableColumns.map((column) => {
          switch (column.columnKey) {
            case EntitlementPageTabsColumnKeys.SUBSCRIPTION:
              return {
                content: renderEntitlementDetails(linkedEntitlement),
              };
            case EntitlementPageTabsColumnKeys.STATUS:
              return {
                content: renderEntitlementStatus(linkedEntitlement),
              };
            // To Verify on Old and new as there was a difference on Content
            case EntitlementPageTabsColumnKeys.APPS:
              return {
                content: (
                  <AppsCount
                    version={isDesignIteration1Enabled ? 2 : undefined}
                    subscription={{
                      id: linkedEntitlement?.entitlementId ?? '',
                      linkedAppsId: linkedEntitlement?.linkedAppsId ?? [],
                    }}
                    baseRoute={subscriptionsRoutePPC}
                  />
                ),
              };
            case EntitlementPageTabsColumnKeys.ORG_NAME:
              return {
                content: (
                  <OrgNameLink
                    orgName={linkedEntitlement?.provisionedResourceDetails?.organisationName}
                    orgId={linkedEntitlement?.provisionedResourceDetails?.organisationId}
                    txaId={transactionAccountId}
                  />
                ),
              };
            case EntitlementPageTabsColumnKeys.SITE:
              return {
                content: renderCloudSiteLink(linkedEntitlement),
              };
            case EntitlementPageTabsColumnKeys.USAGE:
              return {
                content: (
                  <>
                    <div>
                      {linkedEntitlement?.activeUsers ?? 0}{' '}
                      {capitalize(
                        getBilledForColumnData(
                          linkedEntitlement?.productId,
                          linkedEntitlement?.edition,
                          linkedEntitlement?.activeUsers === 1
                        )
                      )}
                    </div>
                    {!isOptedQuantitySourceInherited(linkedEntitlement?.optedQuantitySource) &&
                      linkedEntitlement?.userTier && (
                        <div data-testid="linked-ent-user-tier">User tier - {linkedEntitlement?.userTier}</div>
                      )}
                  </>
                ),
              };
            case EntitlementPageTabsColumnKeys.BILLING_CYCLE:
              return {
                content: linkedEntitlement?.billingFrequency ? (
                  capitalize(linkedEntitlement.billingFrequency)
                ) : (
                  <NoData />
                ),
              };
            case EntitlementPageTabsColumnKeys.CREATED:
              return {
                content: linkedEntitlement?.createdDate ? getFormattedDate(linkedEntitlement.createdDate) : <NoData />,
              };
            case EntitlementPageTabsColumnKeys.ENTITLEMENT_NUMBER:
              return {
                content: linkedEntitlement?.entitlementNumber,
              };
            case EntitlementPageTabsColumnKeys.PLAN:
              return {
                content: (
                  <Stack>
                    <Text>{linkedEntitlement?.edition}</Text>
                    <Text size="small" color="color.text.subtlest">
                      {linkedEntitlement?.billingFrequency ? (
                        capitalize(linkedEntitlement.billingFrequency)
                      ) : (
                        <NoData />
                      )}
                    </Text>
                  </Stack>
                ),
              };
            case EntitlementPageTabsColumnKeys.QUANTITY:
              return {
                content: (
                  <Usage
                    activeUsers={linkedEntitlement?.activeUsers ?? 0}
                    chargeElement={linkedEntitlement?.chargeElement}
                    userTier={linkedEntitlement?.userTier}
                    edition={linkedEntitlement?.edition as string}
                  />
                ),
              };
            case EntitlementPageTabsColumnKeys.RENEW_DATE:
              return {
                content: <NextBillDate entitlement={linkedEntitlement as any} hideChangeBillDateButton />,
              };
            case EntitlementPageTabsColumnKeys.LINKED_SUBSCRIPTIONS:
              return {
                content: (
                  <IncludedProductsCell
                    transactionAccountId={transactionAccountId}
                    product={linkedEntitlement as any}
                    version={isDesignIteration1Enabled ? 2 : undefined}
                    isAppificationEnabled={isAppificationEnabled}
                  />
                ),
              };
          }
        }),
      };
    });
  }, [
    tableViewData.data,
    tableColumns,
    renderEntitlementDetails,
    renderEntitlementStatus,
    isDesignIteration1Enabled,
    subscriptionsRoutePPC,
    transactionAccountId,
    renderCloudSiteLink,
    isAppificationEnabled,
  ]);

  return (
    <DynamicResultTable
      isLoading={loading}
      testId="product-instances-table"
      beforeTable={isDesignIteration1Enabled ? undefined : <TableHeading>{tableHeading}</TableHeading>}
      head={getHead()}
      rows={getRows()}
      emptyStateProps={isDesignIteration1Enabled ? genericEmptyStateProp : emptyStateProp}
      totalCount={linkedEntitlements.length}
      currentPage={tableViewData.currentPage}
      rowsPerPage={tableViewData.rowsPerPage}
      onPageChange={handlePageChange}
      isPaginationEnabled={linkedEntitlements.length > DEFAULT_ROWS_PER_PAGE}
      onRowChange={handleRowSizeChange}
      errorStateProps={errorStateProp}
      error={error?.message}
      hideHeadOnEmptyState={isDesignIteration1Enabled}
    />
  );
};
